import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PublicHeader from "@/components/layout/PublicHeader";
import { useI18n } from "@/context/I18nContext";
import { useSEO } from "@/lib/useSEO";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  GraduationCap,
  Stethoscope,
  Cog,
  TrendingUp,
  CheckCircle2,
  Globe,
  ArrowRight,
  Languages,
} from "lucide-react";

const COPY = {
  en: {
    kicker: "/ Study & Work Abroad",
    h1a: "Study in Europe.",
    h1b: "Work in Germany.",
    sub: "A Maqam pathway — German language certificates, EU-recognized universities, and visa-ready paperwork. From Riyadh to Mannheim, Bucharest, or Berlin.",
    sub2: "Two tracks. One advisor. Zero guesswork.",
    track1Tag: "Track 01 · Languages",
    track1H: "Online language schools, certified by the EU",
    track1Sub: "Learn German online up to A2 with our partner certified center. Once you have the certificate, the German student-visa becomes much easier to secure.",
    eimsBadge: "Primary partner",
    levels: "Levels offered",
    contact: "Contact directly",
    sendEmail: "Send email",
    callNow: "Call now",
    visit: "Visit website",
    addr: "Address",
    track2Tag: "Track 02 · University programs",
    track2H: "EU-recognized degrees in Medicine, Engineering & Finance",
    track2Sub: "Our partner universities in Germany and Romania deliver degrees recognized across the entire European Union. English & German tracks. Visa support included.",
    medicine: "Medicine",
    engineering: "Engineering",
    finance: "Finance",
    duration: "Duration",
    teaching: "Language",
    recognition: "Recognition",
    leadH: "Get a free 1-on-1 advisory call",
    leadSub: "Tell us your track and we'll come back within 24 hours with a tailored plan, fees, and the next intake.",
    fullName: "Full name",
    emailLbl: "Email",
    phone: "WhatsApp / phone",
    country: "Country",
    track: "Which track?",
    msg: "Anything we should know? (optional)",
    consent: "I agree Maqam may contact me about Study & Work Abroad programs.",
    submit: "Request my plan",
    submitting: "Sending…",
    success: "Got it. An advisor will reach out within 24 hours.",
    languageTrack: "Language school (German A1 → C1)",
    medicineTrack: "Study Medicine in EU",
    engineeringTrack: "Study Engineering in EU",
    financeTrack: "Study Finance in EU",
    other: "Other / not sure",
    why: "Why this works",
    why1: "Goethe / telc certificates open the German student visa.",
    why2: "EU-recognized degrees = work permits across 27 countries.",
    why3: "We handle the paperwork — you focus on passing.",
    requiredOnly: "* required",
  },
  ar: {
    kicker: "/ الدراسة والعمل في الخارج",
    h1a: "ادرس في أوروبا.",
    h1b: "اعمل في ألمانيا.",
    sub: "مسار مقام — شهادات لغة ألمانية، جامعات معترف بها في الاتحاد الأوروبي، وأوراق جاهزة للتأشيرة. من الرياض إلى مانهايم أو بوخارست أو برلين.",
    sub2: "مساران. مستشار واحد. صفر تخمين.",
    track1Tag: "المسار 01 · اللغات",
    track1H: "مدارس لغة أونلاين معتمدة من الاتحاد الأوروبي",
    track1Sub: "تعلّم الألمانية أونلاين حتى مستوى A2 مع مركزنا الشريك المعتمد. بعد الحصول على الشهادة، تصبح تأشيرة الطالب الألمانية أسهل بكثير.",
    eimsBadge: "الشريك الرئيسي",
    levels: "المستويات المتاحة",
    contact: "تواصل مباشر",
    sendEmail: "أرسل بريداً",
    callNow: "اتصل الآن",
    visit: "زيارة الموقع",
    addr: "العنوان",
    track2Tag: "المسار 02 · الجامعات",
    track2H: "شهادات معترف بها في الاتحاد الأوروبي — طب، هندسة، مالية",
    track2Sub: "جامعاتنا الشريكة في ألمانيا ورومانيا تمنح شهادات معترف بها في كامل الاتحاد الأوروبي. مسارات إنجليزية وألمانية. دعم تأشيرة كامل.",
    medicine: "طب",
    engineering: "هندسة",
    finance: "مالية",
    duration: "المدة",
    teaching: "لغة الدراسة",
    recognition: "الاعتراف",
    leadH: "احجز استشارة مجانية وجهاً لوجه",
    leadSub: "أخبرنا بمسارك وسنرد خلال 24 ساعة بخطة مخصصة، الرسوم، والدفعة القادمة.",
    fullName: "الاسم الكامل",
    emailLbl: "البريد الإلكتروني",
    phone: "واتساب / هاتف",
    country: "الدولة",
    track: "أي مسار؟",
    msg: "أي ملاحظات؟ (اختياري)",
    consent: "أوافق على تواصل مقام معي بخصوص برامج الدراسة والعمل في الخارج.",
    submit: "اطلب خطتي",
    submitting: "جاري الإرسال…",
    success: "تم الاستلام. سيتواصل معك مستشار خلال 24 ساعة.",
    languageTrack: "مدرسة لغة (ألماني A1 → C1)",
    medicineTrack: "دراسة الطب في الاتحاد الأوروبي",
    engineeringTrack: "دراسة الهندسة في الاتحاد الأوروبي",
    financeTrack: "دراسة المالية في الاتحاد الأوروبي",
    other: "آخر / غير متأكد",
    why: "لماذا هذا يعمل",
    why1: "شهادات Goethe / telc تفتح تأشيرة الطالب الألمانية.",
    why2: "شهادات معترف بها في الاتحاد الأوروبي = تصاريح عمل في 27 دولة.",
    why3: "نتولى الأوراق — وأنت تركّز على النجاح.",
    requiredOnly: "* مطلوب",
  },
};

const TRACK_ICON = {
  medicine: Stethoscope,
  engineering: Cog,
  finance: TrendingUp,
};

function PartnerCard({ p, c }) {
  return (
    <div
      className="bg-[#141416] border border-white/10 p-6 md:p-10 grid md:grid-cols-[220px_1fr] gap-8 md:gap-12"
      data-testid={`partner-card-${p.id}`}
    >
      <div className="flex flex-col items-start gap-3">
        <div className="bg-white p-4 w-full aspect-square flex items-center justify-center">
          <img src={p.logo} alt={p.name} className="max-w-full max-h-full object-contain" />
        </div>
        {p.primary && (
          <span className="font-mono text-[10px] uppercase tracking-widest bg-white text-[#1a1a1c] px-2 py-1">
            {c.eimsBadge}
          </span>
        )}
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">
          {p.tagline}
        </div>
        <h3 className="font-display font-black text-2xl md:text-3xl tracking-tight leading-tight mb-1 text-white">
          {p.name}
        </h3>
        <div className="text-xs font-mono text-white/60 mb-6 flex items-center gap-2">
          <MapPin className="w-3 h-3" /> {p.city}, {p.country}
        </div>

        <ul className="space-y-2 mb-6">
          {p.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2 text-sm text-white/85">
              <CheckCircle2 className="w-4 h-4 mt-0.5 text-white flex-shrink-0" />
              <span>{h}</span>
            </li>
          ))}
        </ul>

        <div className="border-t border-white/10 pt-5 mb-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">
            {c.levels}
          </div>
          <div className="flex flex-wrap gap-2">
            {p.levels.map((l) => (
              <span key={l} className="text-xs font-mono px-2 py-1 border border-white/20 bg-white/5 text-white">
                {l}
              </span>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 pt-5 mb-6 grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">
              {c.addr}
            </div>
            <div className="text-white/90">{p.address}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">
              {c.contact}
            </div>
            <div className="text-white/90">{p.owner}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <a
            href={`mailto:${p.email}?subject=Maqam%20→%20Study%20Abroad%20enquiry`}
            className="inline-flex items-center gap-2 bg-white text-[#1a1a1c] px-5 py-3 text-sm font-bold hover:bg-white/85 transition-colors"
            data-testid={`partner-${p.id}-email`}
          >
            <Mail className="w-4 h-4" /> {c.sendEmail}
          </a>
          <a
            href={`tel:${p.phone.replace(/\s/g, "")}`}
            className="inline-flex items-center gap-2 bg-transparent text-white border border-white/30 px-5 py-3 text-sm font-bold hover:bg-white hover:text-[#1a1a1c] transition-colors"
            data-testid={`partner-${p.id}-call`}
          >
            <Phone className="w-4 h-4" /> {c.callNow}
          </a>
          <a
            href={p.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 text-sm font-bold text-white/70 hover:text-white transition-colors"
            data-testid={`partner-${p.id}-visit`}
          >
            <ExternalLink className="w-4 h-4" /> {c.visit}
          </a>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-2 text-xs font-mono text-white/80">
          <div className="flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-white/60" />
            <span className="break-all">{p.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-3.5 h-3.5 text-white/60" />
            {p.phone}
          </div>
        </div>
      </div>
    </div>
  );
}

function UniColumn({ icon: Icon, title, list, c }) {
  return (
    <div className="bg-[#141416] border border-white/10 p-6 md:p-8" data-testid={`uni-col-${title.toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-white text-[#1a1a1c] p-2.5">
          <Icon className="w-5 h-5" />
        </div>
        <h3 className="font-display font-black text-2xl tracking-tight text-white">{title}</h3>
      </div>
      <div className="space-y-5">
        {list.map((u, i) => (
          <div key={i} className="border-t border-white/10 pt-5 first:border-0 first:pt-0">
            <div className="flex items-center gap-2 mb-1.5 text-xs font-mono uppercase tracking-widest text-white/60">
              <Globe className="w-3 h-3" />
              {u.country}
            </div>
            <div className="font-display font-bold text-lg leading-tight mb-3 text-white">{u.name}</div>
            <div className="grid grid-cols-3 gap-3 text-[11px] font-mono">
              <div>
                <div className="text-white/40 uppercase tracking-widest">{c.teaching}</div>
                <div className="text-white">{u.language}</div>
              </div>
              <div>
                <div className="text-white/40 uppercase tracking-widest">{c.duration}</div>
                <div className="text-white">{u.duration}</div>
              </div>
              <div>
                <div className="text-white/40 uppercase tracking-widest">{c.recognition}</div>
                <div className="text-white">{u.recognition}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StudyAbroad() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "Saudi Arabia",
    track: "language_school",
    message: "",
    consent: true,
  });
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Study & Work Abroad — Maqam",
    description:
      "Study Medicine, Engineering or Finance at EU-recognized universities. Learn German online with EIMS Sprachenschule. Visa-ready certificates. From KSA to Europe.",
    path: "/study-abroad",
  });

  useEffect(() => {
    api.get("/study-abroad/partners").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in name, email, and phone.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/study-abroad/lead", {
        ...form,
        partner: form.track === "language_school" ? "EIMS" : null,
      });
      toast.success(c.success);
      setForm({ ...form, full_name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const eims = data?.language_schools?.[0];
  const med = data?.universities?.medicine || [];
  const eng = data?.universities?.engineering || [];
  const fin = data?.universities?.finance || [];

  return (
    <div className="min-h-screen bg-[#1a1a1c] text-white">
      <PublicHeader />

      {/* HERO */}
      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 mb-4">
            {c.kicker}
          </div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-4xl text-white">
            {c.h1a} <span className="text-white/55">{c.h1b}</span>
          </h1>
          <p className="text-base md:text-lg text-white/80 mt-6 max-w-2xl leading-relaxed">
            {c.sub}
          </p>
          <p className="text-sm md:text-base text-white mt-3 font-mono uppercase tracking-widest">
            {c.sub2}
          </p>

          <div className="mt-12 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-3xl">
            <div className="bg-[#141416] p-5">
              <Languages className="w-5 h-5 text-white mb-3" />
              <div className="font-display font-bold leading-tight text-white">{c.why1}</div>
            </div>
            <div className="bg-[#141416] p-5">
              <GraduationCap className="w-5 h-5 text-white mb-3" />
              <div className="font-display font-bold leading-tight text-white">{c.why2}</div>
            </div>
            <div className="bg-[#141416] p-5">
              <CheckCircle2 className="w-5 h-5 text-white mb-3" />
              <div className="font-display font-bold leading-tight text-white">{c.why3}</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRACK 01 — Languages / EIMS */}
      <section id="languages" className="bg-[#141416] border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 mb-3">
            {c.track1Tag}
          </div>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-tight max-w-3xl mb-4 text-white">
            {c.track1H}
          </h2>
          <p className="text-white/75 max-w-2xl mb-10 leading-relaxed">{c.track1Sub}</p>

          {eims ? (
            <PartnerCard p={eims} c={c} />
          ) : (
            <div className="text-white/50 font-mono text-sm">loading…</div>
          )}
        </div>
      </section>

      {/* TRACK 02 — Universities */}
      <section id="universities" className="bg-[#1a1a1c] border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 mb-3">
            {c.track2Tag}
          </div>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-tight max-w-4xl mb-4 text-white">
            {c.track2H}
          </h2>
          <p className="text-white/75 max-w-2xl mb-10 leading-relaxed">{c.track2Sub}</p>

          <div className="grid md:grid-cols-3 gap-6">
            <UniColumn icon={TRACK_ICON.medicine}    title={c.medicine}    list={med} c={c} />
            <UniColumn icon={TRACK_ICON.engineering} title={c.engineering} list={eng} c={c} />
            <UniColumn icon={TRACK_ICON.finance}     title={c.finance}     list={fin} c={c} />
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section id="apply" className="bg-[#141416]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid lg:grid-cols-[1fr_1.2fr] gap-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 mb-3">
              / Apply
            </div>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-tight mb-5 text-white">
              {c.leadH}
            </h2>
            <p className="text-white/75 leading-relaxed mb-8">{c.leadSub}</p>

            <div className="border-t border-white/10 pt-6 space-y-3 text-sm text-white/85">
              <div className="font-mono text-xs uppercase tracking-widest text-white/60 mb-2">
                {c.why}
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-white flex-shrink-0" /> {c.why1}
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-white flex-shrink-0" /> {c.why2}
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-white flex-shrink-0" /> {c.why3}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="bg-[#1a1a1c] border border-white/10 p-6 md:p-10" data-testid="study-abroad-form">
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <label className="block">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">
                  {c.fullName} *
                </div>
                <input
                  required
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full bg-[#141416] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white"
                  data-testid="sa-name"
                />
              </label>
              <label className="block">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">
                  {c.emailLbl} *
                </div>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-[#141416] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white"
                  data-testid="sa-email"
                />
              </label>
              <label className="block">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">
                  {c.phone} *
                </div>
                <input
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+966 ..."
                  className="w-full bg-[#141416] border border-white/15 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white"
                  data-testid="sa-phone"
                />
              </label>
              <label className="block">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">
                  {c.country}
                </div>
                <input
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="w-full bg-[#141416] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white"
                  data-testid="sa-country"
                />
              </label>
            </div>

            <label className="block mb-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">
                {c.track} *
              </div>
              <select
                value={form.track}
                onChange={(e) => setForm({ ...form, track: e.target.value })}
                className="w-full bg-[#141416] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white"
                data-testid="sa-track"
              >
                <option value="language_school">{c.languageTrack}</option>
                <option value="medicine">{c.medicineTrack}</option>
                <option value="engineering">{c.engineeringTrack}</option>
                <option value="finance">{c.financeTrack}</option>
                <option value="other">{c.other}</option>
              </select>
            </label>

            <label className="block mb-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">
                {c.msg}
              </div>
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-[#141416] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white"
                data-testid="sa-message"
              />
            </label>

            <label className="flex items-start gap-2 mb-5 text-xs text-white/75">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                className="mt-0.5 accent-white"
                data-testid="sa-consent"
              />
              <span>{c.consent}</span>
            </label>

            <button
              type="submit"
              disabled={busy || !form.consent}
              className="w-full bg-white text-[#1a1a1c] py-3.5 font-bold text-sm hover:bg-white/85 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              data-testid="sa-submit"
            >
              {busy ? c.submitting : (
                <>
                  {c.submit} <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            <div className="font-mono text-[10px] text-white/40 mt-3">{c.requiredOnly}</div>
          </form>
        </div>
      </section>
    </div>
  );
}
